import { useEffect, useState } from "react";
import { Plus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/lib/i18n";
import { useStore } from "@/lib/store";
import { api, errorMessage } from "@/lib/api";

const NUM_FIELDS = ["uv_rate", "markup_multiplier", "profit_per_line", "rush_multiplier", "cut_fee", "file_check_fee", "shipping_flat", "free_shipping_over"];
const LABELS = { uv_rate: "uv_rate", markup_multiplier: "markup", profit_per_line: "profit_line", rush_multiplier: "rush_mult", cut_fee: "cut_fee", file_check_fee: "file_fee", shipping_flat: "ship_flat", free_shipping_over: "free_over" };

const PasswordCard = () => {
  const { t } = useLang();
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      toast.success(t("admin.pw_saved"));
      setCur(""); setNw("");
    } catch (ex) {
      toast.error(errorMessage(ex, t));
    }
  };
  return (
    <form onSubmit={submit} className="panel flex flex-col gap-4 p-6" data-testid="password-card">
      <h2 className="text-lg font-extrabold">{t("admin.pw_title")}</h2>
      <label><span className="label">{t("admin.pw_current")}</span><input type="password" value={cur} onChange={(e) => setCur(e.target.value)} className="field" required data-testid="pw-current-input" /></label>
      <label><span className="label">{t("admin.pw_new")}</span><input type="password" minLength={8} value={nw} onChange={(e) => setNw(e.target.value)} className="field" required data-testid="pw-new-input" /></label>
      <button className="btn-ghost" data-testid="pw-save-btn">{t("admin.pw_save")}</button>
    </form>
  );
};

export default function AdminSettings() {
  const { t } = useLang();
  const { setSettings } = useStore();
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    api.get("/settings").then((r) => live && setS((prev) => prev || r.data));
    return () => { live = false; };
  }, []);
  if (!s) return <p className="text-slate-500">{t("common.loading")}</p>;

  const setTier = (i, k, v) => setS((p) => ({ ...p, dtf_tiers: p.dtf_tiers.map((tr, j) => (j === i ? { ...tr, [k]: v } : tr)) }));

  const save = async () => {
    setBusy(true);
    try {
      const { paypal_enabled, ...rest } = s;
      const body = { ...rest, dtf_tiers: s.dtf_tiers.map((tr) => ({ min_area: parseFloat(tr.min_area), rate: parseFloat(tr.rate) })) };
      NUM_FIELDS.forEach((k) => (body[k] = parseFloat(s[k])));
      const { data } = await api.put("/admin/settings", body);
      setS(data);
      setSettings(data);
      toast.success(t("admin.settings_saved"));
    } catch (e) {
      toast.error(errorMessage(e, t));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-4xl" data-testid="admin-settings-page">
      <h1 className="font-display text-3xl sm:text-4xl">{t("admin.settings")}</h1>
      <div className="mt-8 grid gap-6 lg:grid-cols-2">
        <section className="panel p-6">
          <h2 className="mb-4 text-lg font-extrabold">{t("admin.dtf_tiers")}</h2>
          <div className="grid grid-cols-[1fr_1fr_auto] gap-2 text-xs font-bold uppercase text-slate-400"><span>{t("admin.min_area")}</span><span>{t("admin.rate")}</span><span /></div>
          {s.dtf_tiers.map((tr, i) => (
            <div key={i} className="mt-2 grid grid-cols-[1fr_1fr_auto] items-center gap-2" data-testid={`tier-row-${i}`}>
              <input type="number" step="1" min="0" value={tr.min_area} onChange={(e) => setTier(i, "min_area", e.target.value)} className="field font-mono-num" data-testid={`tier-min-${i}`} />
              <input type="number" step="0.001" min="0" value={tr.rate} onChange={(e) => setTier(i, "rate", e.target.value)} className="field font-mono-num" data-testid={`tier-rate-${i}`} />
              <button type="button" disabled={s.dtf_tiers.length <= 1} onClick={() => setS({ ...s, dtf_tiers: s.dtf_tiers.filter((_, j) => j !== i) })} className="p-2 text-slate-500 hover:text-ruby disabled:opacity-30" data-testid={`tier-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
          <button type="button" onClick={() => setS({ ...s, dtf_tiers: [...s.dtf_tiers, { min_area: 0, rate: 0.01 }] })} className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-gold" data-testid="tier-add-btn"><Plus className="h-4 w-4" /> {t("admin.add_tier")}</button>
        </section>
        <section className="panel grid gap-4 p-6 sm:grid-cols-2">
          {NUM_FIELDS.map((k) => (
            <label key={k} className={k === "markup_multiplier" ? "sm:col-span-2" : ""}>
              <span className="label">{t(`admin.${LABELS[k]}`)}</span>
              <input type="number" step="0.01" min="0" value={s[k]} onChange={(e) => { const v = e.target.value; setS((p) => ({ ...p, [k]: v })); }} className={`field font-mono-num ${["markup_multiplier", "profit_per_line"].includes(k) ? "border-emerald-500/40" : ""}`} data-testid={`setting-${k}`} />
            </label>
          ))}
        </section>
      </div>
      <button onClick={save} disabled={busy} className="btn-gold mt-6" data-testid="save-settings-btn">{busy && <Loader2 className="h-4 w-4 animate-spin" />} {t("admin.save_settings")}</button>
      <div className="mt-12 max-w-md"><PasswordCard /></div>
    </div>
  );
}
